import type { Currency } from '@swf/core/enums';
import type { CasinoIcon } from '@swf/generated/types/common/Casino';

export enum Feature {
  Jackpot = 1,
  EvoVIP = 2,
  EvoFT = 4,
  FreeSpin = 8,
  BonusRnd = 16,
  MGA = 32,
  IGT = 64
}

export enum GameService {
  Recommended = 'recommended',
  Recent = 'recent',
  Favorite = 'favorites'
}

export enum MarketingTag {
  Race = 'Race',
  Exclusive = 'Exclusive',
  New = 'New',
  Multiplier100 = 'multiplier100',
  Multiplier300 = 'multiplier300',
  Multiplier500 = 'multiplier500',
  Multiplier2000 = 'multiplier2000',
  Multiplier10000 = 'multiplier10000',
  Multiplier20000 = 'multiplier20000',
  Hot = 'Hot',
  HighRoller = 'High Roller',
  LiveLabel = 'Live Label',
  Slots = 'Slots',
  GameInfo = 'GameInfo'
}

export enum MarketingOverlayTag {
  Exclusive = 'o_Exclusive'
}

export const marketingOverlays = (
  tags: string[] = Object.values(MarketingOverlayTag),
  invert = false
) => tags.filter((tag) => (invert && !tag.startsWith('o_')) || (!invert && tag.startsWith('o_')));

export const marketingOverlaySiteName = (siteName: string) =>
  marketingOverlays().map((v) => `${v} ${siteName}`);

export const marketingMultipliers = Object.values(MarketingTag).filter((tag) =>
  tag.startsWith('multiplier')
);

export const marketingMultipliersSiteName = (siteName: string) =>
  marketingMultipliers.map((v) => `${v} ${siteName}`);

export enum PlayType {
  Single = 1,
  Multi = 2
}

export enum JoinReason {
  Play = 0,
  Observe = 1,
  Resume = 3
}

export interface CasinoGame {
  jackpot?: Jackpot;
  name: string;
  images: CasinoGameImage;
  gametype: number;
  variant: number;
  isFavorite?: boolean;
  tags?: string[];
  tables: CasinoTable[];
  feature?: Feature;
  playCount?: number;
  desc?: string;
  fsb?: boolean;
}

export interface LiveStatus {
  numPlayers: number;
  open?: boolean;
  openingDateTime?: number;
  multiplier?: number;
  show?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface LiveCasinoGame extends CasinoGame {
  currency: Currency;
  minBet: number;
  maxBet: number;
  tId: number;
  ttId: number;
  maxSeats?: number;
  statusMeta: LiveStatus;
}

export interface CasinoTable {
  currency: Currency;
  features: number;
  fsbId?: number;
  id: number;
  maxBet: number;
  maxSeats: number;
  numPlayers?: number;
  minBet: number;
  playType: PlayType;
  playMoney?: number;
  featureNames?: string[];
  properties?: {
    lines: string;
    holdRate: number;
  };
}

export interface CasinoGameInstance {
  cur: string;
  fsbId: number;
  ftrs: number;
  gSet: {
    lUrl: string;
    initp: string;
  };
  gType: number;
  lm: number;
  maxBet: number;
  maxS: number;
  mbin: number;
  minBet: number;
  numOf: number;
  pFF: number;
  pType: number;
  sAddr: string;
  sInst: string;
  sNum: number;
  tId: number;
  tNm: string;
  tech: number;
  ttId: number;
  ufgId: number;
  uid: string;
  url: string;
  vId: number;
  vName: string;
  vType: number;
  xtk?: string;
  launchKey?: string;
}

export interface CasinoInterruptedGame {
  created: number;
  currency: Currency;
  features: number;
  fsbId: number;
  gametype: number;
  id: number;
  launchMode: number;
  lsource: number;
  maxBet: number;
  minBet: number;
  name: string;
  oBet: number;
  platforms: number;
  playCount: number;
  playMoney: number;
  tId: number;
  tech: number;
  ufgId: number;
  uid: string;
  vId: number;
  variant: number;
  xtk: string;
}

export type CasinoGameImage = { [I in CasinoIcon]?: string };

export interface PlayerFSB {
  userRollId: number;
  ttId: number;
  betPerSpin: number;
  totalSpins: number;
  remainingSpins: number;
  currency: Currency;
}

export interface Jackpot {
  currency: Currency;
  value: number;
  key: number;
}

export interface StreamData<T extends CasinoGame> {
  games: T[];
  subscriptions: { [path: string]: string };
}

export interface Page {
  complete: boolean;
  games: CasinoGame[];
  page: number;
}

export enum MosaicContext {
  None,
  BigIcon
}
